import React, { useState, useEffect } from 'react';
import { Sparkles, Activity, BarChart2, Shield, User, TrendingDown, Layers, Crosshair, ArrowUpRight, Eye, AlertTriangle, Zap, TrendingUp, Send } from 'lucide-react';
import './AskAnythingSection.scss';

type AnalysisType = 'Momentum Analysis' | 'Oversold Screener' | 'Fundamentals Compare' | 'Nifty Outlook';

interface ResponseCardData {
  title: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  iconBgClass: string;
}

interface PromptData {
  id: AnalysisType;
  plainText: string;
  chips: string[];
  cards: ResponseCardData[];
}

const HIGHLIGHT_WORDS = ['RELIANCE.NS', 'RSI', 'VWAP', 'HDFCBANK', 'TCS', 'INFY', 'NIFTY50'];

const PROMPTS_DATA: PromptData[] = [
  {
    id: 'Momentum Analysis',
    plainText: 'Analyze RELIANCE.NS momentum and suggest entry points based on RSI and VWAP',
    chips: ['Market', 'Stocks', 'Futures'],
    cards: [
      { title: 'Momentum Score', value: '74 / 100 — Bullish', sub: 'RSI(14) at 58.3 · VWAP ₹2,847', icon: <Activity size={16} color="#4ade80" />, iconBgClass: 'green-bg' },
      { title: 'Entry Zone', value: '₹2,810 — ₹2,830', sub: 'Stop-loss ₹2,770 · Target ₹2,980', icon: <BarChart2 size={16} color="#60a5fa" />, iconBgClass: 'blue-bg' },
      { title: 'Risk Rating', value: 'Moderate', sub: 'ATR 42.1 · Beta 0.91', icon: <Shield size={16} color="#fbbf24" />, iconBgClass: 'orange-bg' }
    ]
  },
  {
    id: 'Oversold Screener',
    plainText: 'Find oversold Nifty 50 stocks with high delivery volume and RSI below 30',
    chips: ['Screener', 'Stocks', 'Volume'],
    cards: [
      { title: 'Top Match', value: 'HDFCBANK', sub: 'RSI(14) at 28.4 — Strongly Oversold', icon: <Crosshair size={16} color="#f87171" />, iconBgClass: 'red-bg' },
      { title: 'Volume Surge', value: '+45% Delivery', sub: 'Compared to 10-day moving average', icon: <TrendingDown size={16} color="#fbbf24" />, iconBgClass: 'orange-bg' },
      { title: 'Reversal Prob.', value: 'High (82%)', sub: 'Historical bounce rate from this level', icon: <Activity size={16} color="#4ade80" />, iconBgClass: 'green-bg' }
    ]
  },
  {
    id: 'Fundamentals Compare',
    plainText: 'Compare TCS and INFY based on operating margins and free cash flow yield over 3 years',
    chips: ['Fundamentals', 'IT Sector', 'Compare'],
    cards: [
      { title: 'Op Margin', value: 'TCS 24.5% > INFY 21.0%', sub: 'Trailing 12-month average', icon: <Layers size={16} color="#60a5fa" />, iconBgClass: 'blue-bg' },
      { title: 'FCF Yield', value: 'TCS 3.8% > INFY 3.2%', sub: 'Higher cash generation capability', icon: <BarChart2 size={16} color="#4ade80" />, iconBgClass: 'green-bg' },
      { title: 'Valuation Gap', value: 'TCS Premium 15%', sub: 'Based on Forward P/E multiples', icon: <Eye size={16} color="#a855f7" />, iconBgClass: 'purple-bg' }
    ]
  },
  {
    id: 'Nifty Outlook',
    plainText: 'What is the short-term outlook for NIFTY50 based on current option chain data?',
    chips: ['Indices', 'Derivatives', 'Options'],
    cards: [
      { title: 'Trend', value: 'Sideways to Bullish', sub: 'Put-Call Ratio (PCR) at 1.15', icon: <ArrowUpRight size={16} color="#4ade80" />, iconBgClass: 'green-bg' },
      { title: 'Key Support', value: '22,000 Level', sub: 'Highest Put Open Interest', icon: <Shield size={16} color="#60a5fa" />, iconBgClass: 'blue-bg' },
      { title: 'Resistance', value: '22,500 Level', sub: 'Aggressive Call writing observed', icon: <AlertTriangle size={16} color="#fbbf24" />, iconBgClass: 'orange-bg' }
    ]
  }
];

const renderHighlightedText = (text: string) => {
  if (!text) return null;
  const regexPattern = new RegExp(`\\b(${HIGHLIGHT_WORDS.map(w => w.replace('.', '\\.')).join('|')})\\b`, 'gi');
  const parts = text.split(regexPattern);

  return (
    <>
      {parts.map((part, i) => {
        const isHighlight = HIGHLIGHT_WORDS.some(hw => hw.toLowerCase() === part.toLowerCase());
        return isHighlight ? (
          <span key={i} className="highlight-indicator">{part}</span>
        ) : (
          part
        );
      })}
    </>
  );
};

const AskAnythingSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [phase, setPhase] = useState<'typing' | 'loading' | 'revealed' | 'paused'>('typing');

  const TYPING_SPEED = 30; // ms per char
  const LOADING_DURATION = 1500;
  const REVEAL_DURATION = 5000;
  const PAUSE_DURATION = 1000;

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (phase === 'typing') {
      const fullText = PROMPTS_DATA[currentIndex].plainText;
      if (displayedText.length < fullText.length) {
        timeout = setTimeout(() => {
          setDisplayedText(fullText.slice(0, displayedText.length + 1));
        }, TYPING_SPEED);
      } else {
        timeout = setTimeout(() => setPhase('loading'), 300);
      }
    } else if (phase === 'loading') {
      timeout = setTimeout(() => setPhase('revealed'), LOADING_DURATION);
    } else if (phase === 'revealed') {
      timeout = setTimeout(() => setPhase('paused'), REVEAL_DURATION);
    } else if (phase === 'paused') {
      timeout = setTimeout(() => {
        setDisplayedText('');
        setCurrentIndex((prev) => (prev + 1) % PROMPTS_DATA.length);
        setPhase('typing');
      }, PAUSE_DURATION);
    }

    return () => clearTimeout(timeout);
  }, [phase, displayedText, currentIndex]);

  const activePrompt = PROMPTS_DATA[currentIndex];

  const handlePillClick = (index: number) => {
    setCurrentIndex(index);
    setDisplayedText('');
    setPhase('typing');
  };

  return (
    <section className="ask-anything-section">
      <div className="ask-container">
        
        <div className="windows-layout">
          
          {/* Text Description Section */}
          <div className="ask-header">
            <div className="ai-badge">
              <Sparkles size={14} />
              <span>AI-POWERED</span>
            </div>
            <h2 className="title-white">Ask anything.</h2>
            <h2 className="title-gradient">Get answers instantly.</h2>
            <p className="subtitle">
              Our AI understands financial context. Describe what you need in plain<br className="hide-mobile" />
              English — technical analysis, stock screeners, futures outlooks — and<br className="hide-mobile" />
              get structured, actionable insights in seconds.
            </p>
          </div>
          
          {/* YOU ASK Section */}
          <div className="window-column">
            <h4 className="column-title">YOU ASK</h4>
            <div className="mock-window you-ask-window">
              <div className="window-topbar">
                <div className="mac-dots">
                  <span className="mac-dot mac-dot-red"></span>
                  <span className="mac-dot mac-dot-yellow"></span>
                  <span className="mac-dot mac-dot-green"></span>
                </div>
                <div className="window-title">algofabric.ai / ask</div>
                <div className="window-icon"><Sparkles size={14} color="#3b82f6" /></div>
              </div>
              
              <div className="window-content prompt-content">
                <div className="prompt-header">
                  <div className="avatar-icon">
                    <User size={16} />
                  </div>
                  <span className="prompt-label">AI PROMPT</span>
                </div>
                <p className="prompt-text">
                  {renderHighlightedText(displayedText)}
                  {phase === 'typing' && <span className="typing-cursor">|</span>}
                </p>
              </div>

              <div className="window-footer prompt-footer">
                <div className="chip-group">
                  {activePrompt.chips.map(chip => (
                    <span key={chip} className="chip">{chip}</span>
                  ))}
                </div>
                <div className="submit-button">
                  <Send size={14} color="#3b82f6" style={{ marginLeft: '-1px', marginTop: '1px' }} />
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Navigation Pills */}
        <div className="analysis-pills">
          {PROMPTS_DATA.map((opt, idx) => (
            <button
              key={opt.id}
              className={`analysis-pill ${currentIndex === idx ? 'active' : ''}`}
              onClick={() => handlePillClick(idx)}
            >
              {opt.id}
            </button>
          ))}
        </div>

        {/* Bottom Feature Cards */}
        <div className="bottom-feature-cards">
          <div className="feature-card">
            <Zap size={18} color="#60a5fa" className="fc-icon" />
            <div className="fc-title">Instant Analysis</div>
            <div className="fc-sub">Results in under 2 seconds</div>
          </div>
          <div className="feature-card">
            <BarChart2 size={18} color="#60a5fa" className="fc-icon" />
            <div className="fc-title">150+ Indicators</div>
            <div className="fc-sub">RSI, MACD, VWAP & more</div>
          </div>
          <div className="feature-card">
            <Shield size={18} color="#60a5fa" className="fc-icon" />
            <div className="fc-title">NSE / BSE Live</div>
            <div className="fc-sub">Real-time market data feeds</div>
          </div>
          <div className="feature-card">
            <TrendingUp size={18} color="#60a5fa" className="fc-icon" />
            <div className="fc-title">NLP Driven</div>
            <div className="fc-sub">Plain English queries</div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AskAnythingSection;
